import { Divider, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { FilterValueStatus, GetCommonTemplatesQueryParameters, GetProjectTenantVariablesQueryParameters, LibraryVariableSetResource, GetProjectTemplateQueryParameters, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { FilterValueStatusValues } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { FilterActions } from "~/areas/projects/components/Variables/TenantVariables/FilterActions";
import { styles } from "~/areas/projects/components/Variables/TenantVariables/styles";
import type { TenantVariableFiltersData } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableFiltersData";
import CollapsibleFilter from "~/areas/tenants/components/HeaderBar/CollapsibleFilter";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import FilterSearchBox from "~/components/FilterSearchBox/FilterSearchBox";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { LibraryVariableSetMultiSelect } from "~/components/MultiSelect/LibraryVariableSetMultiSelect";
import { ProjectMultiSelect } from "~/components/MultiSelect/ProjectMultiSelect";
import { TenantMultiSelect } from "~/components/MultiSelect/TenantMultiSelect";
import { TenantTagMultiSelect } from "~/components/MultiSelect/TenantTagMultiSelect";
export type ProjectTenantVariablesFilterState = GetProjectTenantVariablesQueryParameters & GetCommonTemplatesQueryParameters & GetProjectTemplateQueryParameters & {
    pageNumber: number;
};
interface FiltersBarProps {
    totalVariablesCount: number;
    filteredVariablesCount: number | null;
    filterState: ProjectTenantVariablesFilterState;
    onFilterChange: (newFilterState: ProjectTenantVariablesFilterState) => void;
    filterData: TenantVariableFiltersData;
    unsavedChangesCount?: number;
    libraryVariableSets?: LibraryVariableSetResource[] | null;
    projects?: ProjectResource[] | null;
    doBusyTask: DoBusyTask;
}
export function ProjectTenantVariableTemplatesFiltersBar({ filterState, onFilterChange, totalVariablesCount, filteredVariablesCount, filterData, unsavedChangesCount, doBusyTask }: FiltersBarProps) {
    return (<FiltersBar totalVariablesCount={totalVariablesCount} filteredVariablesCount={filteredVariablesCount} filterState={filterState} onFilterChange={onFilterChange} filterData={filterData} unsavedChangesCount={unsavedChangesCount} doBusyTask={doBusyTask}/>);
}
export function TemplatesFiltersBar({ filterState, onFilterChange, totalVariablesCount, filteredVariablesCount, filterData, unsavedChangesCount, libraryVariableSets, projects, doBusyTask }: FiltersBarProps) {
    return (<FiltersBar totalVariablesCount={totalVariablesCount} filteredVariablesCount={filteredVariablesCount} filterState={filterState} onFilterChange={onFilterChange} filterData={filterData} unsavedChangesCount={unsavedChangesCount} libraryVariableSets={libraryVariableSets} projects={projects} doBusyTask={doBusyTask}/>);
}
function FiltersBar({ filterState, onFilterChange, totalVariablesCount, filteredVariablesCount, unsavedChangesCount, libraryVariableSets, filterData, projects, doBusyTask }: FiltersBarProps) {
    const handleNameFilterChange = (newName: string) => {
        onFilterChange({
            ...filterState,
            pageNumber: 1,
            filterByName: newName,
        });
    };
    const handleValueStatusFilterChange = (newValueStatus: FilterValueStatus) => {
        onFilterChange({
            ...filterState,
            pageNumber: 1,
            filterByValueStatus: newValueStatus,
        });
    };
    const allTags = filterData.tagSets?.flatMap((ts) => ts.Tags) ?? [];
    const selectedTagCanonicalNames = (filterState.filterByTags ?? []).map((id) => allTags.find((t) => t.Id === id)?.CanonicalTagName).filter((cn): cn is string => cn !== undefined);
    const handleTagsChange = (canonicalNames: string[]) => {
        const tagIds = canonicalNames.map((cn) => allTags.find((t) => t.CanonicalTagName === cn)?.Id).filter((id): id is string => id !== undefined);
        onFilterChange({ ...filterState, pageNumber: 1, filterByTags: tagIds });
    };
    return (<CollapsibleFilter totalCount={totalVariablesCount} filteredCount={filteredVariablesCount === totalVariablesCount ? null : filteredVariablesCount} entityName={"value"} actions={<FilterActions unsavedChanges={unsavedChangesCount}/>} secondaryContent={<FilterSearchBox placeholder={"Filter by variable name..."} value={filterState.filterByName} onChange={handleNameFilterChange} autoFocus={true}/>}>
            <div className={styles.filterContainer}>
                <div className={styles.filterSidebar}>
                    <RadioButtonGroup value={filterState.filterByValueStatus ?? FilterValueStatusValues.All} onChange={handleValueStatusFilterChange} title="Show">
                        <RadioButton label="All" value={FilterValueStatusValues.All}/>
                        <RadioButton label="Non-default" value={FilterValueStatusValues.Overridden}/>
                        <RadioButton label="Missing" value={FilterValueStatusValues.Missing}/>
                    </RadioButtonGroup>
                </div>
                <Divider orientation={"vertical"}/>
                <div className={styles.filterMultiSelectContainer}>
                    {filterData.environments && (<div className={styles.filterItem}>
                            <EnvironmentMultiSelect value={filterState.filterByEnvironments || []} items={filterData.environments} onChange={(values) => onFilterChange({ ...filterState, pageNumber: 1, filterByEnvironments: values })} label="Environment" accessibleName="Filter by environment"/>
                        </div>)}

                    {filterData.tenants && (<div className={styles.filterItem}>
                            <TenantMultiSelect value={filterState.filterByTenants || []} items={Array.from(filterData.tenants.values())} onChange={(values) => onFilterChange({ ...filterState, pageNumber: 1, filterByTenants: values })} label="Tenant" accessibleName="Filter by tenant"/>
                        </div>)}

                    {filterData.tagSets &&
            filterData.tagSets
                .filter((tagSet) => tagSet.Tags.length > 0)
                .map((tagSet) => (<div key={tagSet.Id} className={styles.filterItem}>
                                    <TenantTagMultiSelect value={selectedTagCanonicalNames} items={[tagSet]} onChange={handleTagsChange} doBusyTask={doBusyTask}/>
                                </div>))}

                    {libraryVariableSets && (<div className={styles.filterItem}>
                            <LibraryVariableSetMultiSelect value={filterState.filterByLibraryVariableSetIds || []} items={libraryVariableSets} onChange={(values) => onFilterChange({ ...filterState, pageNumber: 1, filterByLibraryVariableSetIds: values })} label="Library Variable Set" accessibleName="Filter by library variable set"/>
                        </div>)}

                    {projects && (<div className={styles.filterItem}>
                            <ProjectMultiSelect value={filterState.filterByProjectIds || []} items={projects} onChange={(values) => onFilterChange({ ...filterState, pageNumber: 1, filterByProjectIds: values })} label="Project" accessibleName="Filter by project"/>
                        </div>)}
                </div>
            </div>
        </CollapsibleFilter>);
}
